const STORAGE_KEY = 'pixlie_attribution_v1';

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  ref?: string;
  captured_at: string;
}

const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'ref',
] as const;

type AttributionKey = (typeof ATTRIBUTION_KEYS)[number];

function readParams(search: string): Partial<Record<AttributionKey, string>> {
  const params = new URLSearchParams(search);
  const partial: Partial<Record<AttributionKey, string>> = {};
  for (const key of ATTRIBUTION_KEYS) {
    const value = params.get(key);
    if (value) {
      partial[key] = value;
    }
  }
  return partial;
}

/** Persist UTM/ref from the current URL (first-touch in session). */
export function captureAttributionFromUrl(search?: string): Attribution | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const partial = readParams(search ?? window.location.search);
  if (Object.keys(partial).length === 0) {
    return loadAttribution();
  }

  const attr: Attribution = {
    ...partial,
    captured_at: new Date().toISOString(),
  };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attr));

  const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag;
  if (gtag) {
    gtag('set', 'user_properties', {
      utm_source: attr.utm_source ?? '',
      utm_medium: attr.utm_medium ?? '',
      utm_campaign: attr.utm_campaign ?? '',
      utm_content: attr.utm_content ?? '',
      ref: attr.ref ?? '',
    });
  }

  return attr;
}

export function loadAttribution(): Attribution | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
}

export function formatAttributionLabel(attr: Attribution): string {
  return [attr.ref, attr.utm_campaign, attr.utm_content].filter(Boolean).join(' · ');
}
